// 送信前確認ダイアログ — AIダイジェスト送信フローの safety gate。
// タブ:
//   概要      : 種類 / 期間 / 宛先リスト / 添付ファイル名 / 生成者
//   プレビュー: <embed> で /preview.pdf を同一オリジンで表示
//
// 使用例:
//   const [dlgRun, setDlgRun] = useState(null);
//   <ConfirmSendDialog run={dlgRun} onClose={() => setDlgRun(null)}
//                       onSent={(res) => { toast.success('送信しました'); setDlgRun(null); }} />
//
// props.run は POST /api/digest-runs で作成された行 (status=pending_confirmation)。
// 送信ボタンは処理中は無効化 — 二重送信を防ぐ。
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '@/auth/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Paperclip, Warning, ArrowsClockwise, PaperPlaneTilt, X } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const KIND_LABEL = { project_weekly: '週間プロジェクトダイジェスト', daily_report: '日報ダイジェスト', eng_progress: '進捗レポート' };

export default function ConfirmSendDialog({ run, onClose, onSent, testMode = false }) {
  const [tab, setTab] = useState('summary');
  const [sending, setSending] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [toc, setToc] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    // Whenever the dialog opens with a new run, invalidate any prior blob URL
    // + reset page nav state.
    setPdfUrl(null);
    setPageCount(0);
    setToc([]);
    setCurrentPage(1);
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [run?.id]);

  useEffect(() => {
    if (!run?.id) return;
    if (tab !== 'preview') return;
    if (pdfUrl) return;
    // Fetch as blob so the browser doesn't reissue an auth-less request
    // (cookies flow via withCredentials). Fire meta call in parallel so the
    // page-nav pills appear as soon as the iframe is ready.
    Promise.all([
      axios.get(`${API}/api/digest-runs/${run.id}/preview.pdf`, {
        responseType: 'blob',
        withCredentials: true,
      }),
      axios.get(`${API}/api/digest-runs/${run.id}/preview_meta`, {
        withCredentials: true,
      }).catch(() => ({ data: { page_count: 0, toc: [] } })),   // meta failure is non-fatal
    ]).then(([blobRes, metaRes]) => {
      const url = URL.createObjectURL(blobRes.data);
      objectUrlRef.current = url;
      setPdfUrl(url);
      setPageCount(Number(metaRes.data?.page_count) || 0);
      setToc(Array.isArray(metaRes.data?.toc) ? metaRes.data.toc : []);
    }).catch((e) => {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'PDFの生成に失敗しました。時間をおいて再度お試しください。');
      setPdfUrl('error');
    });
  }, [tab, run?.id, pdfUrl]);

  if (!run) return null;

  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/digest-runs/${run.id}/confirm-send`, { test: !!testMode }, { withCredentials: true });
      onSent?.(r.data);
    } catch (e) {
      // 409 = duplicate / bad state, 502 = SMTP failure
      const detail = formatApiErrorDetail(e?.response?.data?.detail) || 'メール送信に失敗しました。送信先と添付ファイルを確認してください。';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const attachmentName = `digest_${run.kind}_${run.period_start}_${run.period_end}.pdf`;
  // 週間AIダイジェストを「別PDF」「両方」で出す指定のときだけ2つ目の添付が付く。
  const hasWeeklyDigestPdf = ((run.payload_snapshot || {}).weekly_digests_separate || []).length > 0;
  const wdAttachmentName = `weekly_digest_${run.period_start}_${run.period_end}.pdf`;

  return (
    <Dialog open={!!run} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl p-0 gap-0" data-testid="confirm-send-dialog">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-zinc-100">
          <DialogTitle className="text-[15px] font-bold text-zinc-900">送信前確認</DialogTitle>
          <DialogDescription className="text-[11.5px] text-zinc-500 mt-0.5">
            以下の内容でダイジェストメールを送信します。内容と送信先を確認してください。
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-5 pt-2 border-b border-zinc-100">
          {[
            { v: 'summary', label: '概要' },
            { v: 'preview', label: 'プレビュー (PDF)' },
          ].map((t) => (
            <button key={t.v} type="button" onClick={() => setTab(t.v)}
              data-testid={`confirm-send-tab-${t.v}`}
              className={`text-[12px] font-semibold px-3 py-2 border-b-2 transition-colors ${
                tab === t.v
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}>{t.label}</button>
          ))}
        </div>

        {tab === 'summary' && (
          <div className="px-5 py-4 space-y-3 text-[12.5px]" data-testid="confirm-send-summary">
            <dl className="grid grid-cols-[110px_1fr] gap-x-4 gap-y-2 text-zinc-700">
              <dt className="text-zinc-400 text-[11.5px]">種類</dt>
              <dd className="font-semibold">{KIND_LABEL[run.kind] || run.kind}</dd>
              <dt className="text-zinc-400 text-[11.5px]">対象期間</dt>
              <dd className="tabular-nums">{run.period_start} 〜 {run.period_end}</dd>
              <dt className="text-zinc-400 text-[11.5px]">件名</dt>
              <dd className="text-zinc-800 truncate" title={run.email_subject}>{run.email_subject || '（既定）'}</dd>
              <dt className="text-zinc-400 text-[11.5px]">添付ファイル</dt>
              <dd className="inline-flex flex-wrap items-center gap-1.5 text-zinc-700">
                <Paperclip size={12} /> <span className="font-mono">{attachmentName}</span>
                <span className="text-zinc-400 text-[10.5px]">(送信時にPDF生成)</span>
                {hasWeeklyDigestPdf && (
                  <span className="inline-flex items-center gap-1.5 basis-full mt-1"
                    data-testid="confirm-send-wd-attachment">
                    <Paperclip size={12} /> <span className="font-mono">{wdAttachmentName}</span>
                    <a href={`${API}/api/digest-runs/${run.id}/weekly-digest.pdf`}
                      target="_blank" rel="noreferrer"
                      data-testid="confirm-send-wd-preview-link"
                      className="text-[10.5px] underline text-teal-700 hover:text-teal-900">
                      プレビュー
                    </a>
                  </span>
                )}
              </dd>
              <dt className="text-zinc-400 text-[11.5px]">生成者</dt>
              <dd>{run.generated_by?.name || '—'}</dd>
            </dl>
            <div>
              <p className="text-[11.5px] font-semibold text-zinc-500 mb-1.5">
                送信先 ({(run.recipients || []).length} 名)
              </p>
              <div className="max-h-32 overflow-y-auto rounded-lg border border-zinc-200 bg-zinc-50 divide-y divide-zinc-100">
                {(run.recipients || []).map((email) => (
                  <div key={email} className="px-3 py-1.5 text-[11.5px] font-mono text-zinc-700" data-testid={`confirm-send-recipient-${email}`}>
                    {email}
                  </div>
                ))}
              </div>
            </div>
            {testMode && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <Warning size={11} weight="fill" className="inline mr-1" />
                テスト送信モードです。件名に「【テスト送信】」が付きます。
              </p>
            )}
          </div>
        )}

        {tab === 'preview' && (
          <div className="px-5 py-4" data-testid="confirm-send-preview">
            {!pdfUrl && (
              <div className="h-[420px] flex items-center justify-center text-[12px] text-zinc-400">
                <ArrowsClockwise size={16} className="animate-spin mr-2" /> PDFを生成しています…
              </div>
            )}
            {pdfUrl === 'error' && (
              <div className="h-[420px] flex items-center justify-center text-[12px] text-rose-600">
                <Warning size={14} className="mr-1.5" />
                PDFの生成に失敗しました。時間をおいて再度お試しください。
              </div>
            )}
            {pdfUrl && pdfUrl !== 'error' && (
              <>
                {/* Section TOC — mini navigator built from H1/H2/H3 in the
                    source markdown, each resolved to its PDF page number via
                    PyMuPDF text-search. Only shown when there's more than one
                    entry with a real page anchor. Clicking jumps to that
                    section via the same #page=N mechanism as the pill strip. */}
                {toc.filter((t) => t.page > 0).length > 1 && (
                  <div className="mb-2 rounded-lg border border-zinc-200 bg-zinc-50/60 px-2.5 py-2" data-testid="preview-toc">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">目次</p>
                    <div className="flex flex-wrap gap-1">
                      {toc.filter((t) => t.page > 0).map((t, i) => (
                        <button
                          key={`${i}-${t.title}`}
                          type="button"
                          onClick={() => setCurrentPage(t.page)}
                          data-testid={`preview-toc-${i}`}
                          title={`${t.title} (p.${t.page})`}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] transition-colors ${
                            t.level === 1 ? 'font-bold' : t.level === 2 ? 'font-semibold' : ''
                          } ${
                            currentPage === t.page
                              ? 'bg-teal-600 border-teal-600 text-white'
                              : 'bg-white border-zinc-200 text-zinc-700 hover:bg-teal-50 hover:border-teal-300'
                          } ${t.level === 3 ? 'opacity-80' : ''}`}
                          style={t.level === 3 ? { marginLeft: 8 } : undefined}>
                          <span className="truncate max-w-[180px]">{t.title}</span>
                          <span className={`tabular-nums ${currentPage === t.page ? 'text-teal-100' : 'text-zinc-400'}`}>p.{t.page}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Page-number pill strip. Complements the TOC — TOC groups by
                    section, this jumps by raw page. Only shown when >1 page. */}
                {pageCount > 1 && (
                  <div className="relative mb-2" data-testid="preview-page-nav">
                    <div className="flex items-center gap-1 overflow-x-auto pb-0.5">
                      <span className="text-[10.5px] text-zinc-400 shrink-0 mr-1">
                        {pageCount} ページ · ジャンプ:
                      </span>
                      {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setCurrentPage(p)}
                          data-testid={`preview-page-pill-${p}`}
                          className={`shrink-0 h-6 min-w-[26px] px-2 rounded-full text-[10.5px] font-semibold tabular-nums border transition-colors ${
                            currentPage === p
                              ? 'bg-teal-600 border-teal-600 text-white'
                              : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:border-zinc-300'
                          }`}>{p}</button>
                      ))}
                    </div>
                  </div>
                )}
                <iframe
                  key={currentPage}
                  src={`${pdfUrl}#page=${currentPage}`}
                  title="digest-preview"
                  className="w-full h-[520px] rounded-lg border border-zinc-200 bg-zinc-50"
                  data-testid="confirm-send-preview-embed"
                />
                <p className="mt-2 text-[10.5px] text-zinc-400 text-center">
                  プレビューが表示されない場合は{' '}
                  <a href={pdfUrl} download={attachmentName} className="underline text-teal-700 hover:text-teal-900">
                    PDFをダウンロード
                  </a>{' '}
                  してご確認ください。
                </p>
              </>
            )}
          </div>
        )}

        <DialogFooter className="px-5 py-3 border-t border-zinc-100 gap-2 sm:justify-between">
          <p className="text-[10.5px] text-zinc-400">
            この操作を実行すると、選択された送信先にメールが送信されます。
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={sending}
              data-testid="confirm-send-cancel-btn">
              <X size={13} className="mr-1" /> キャンセル
            </Button>
            <Button onClick={send} disabled={sending}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="confirm-send-submit-btn">
              {sending
                ? <><ArrowsClockwise size={13} className="mr-1 animate-spin" /> 送信中…</>
                : <><PaperPlaneTilt size={13} className="mr-1" /> {testMode ? 'テスト送信' : '送信する'}</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
